import React, { useEffect, useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, CalendarIcon, PercentIcon, InfoIcon, TagIcon, TagsIcon, TicketIcon, BanknoteIcon, RefreshCwIcon, CreditCardIcon, GlobeIcon, HashIcon, LayersIcon, SearchIcon, PlusIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { Campaign } from '../../../shared/types/Campaign';
import { FormField } from '../../../shared/ui/FormField';
import { SearchableSelect } from '../../../shared/ui/SearchableSelect';
// Mock campaigns data - would be replaced with API calls in a real app
const mockCampaigns: Record<string, any> = {
  BILASHOP2025: {
    id: 'BILASHOP2025',
    title: '[BILA Mart] 7-days Promotion for Shop',
    description: '[BILA Mart] 7-days Promotion for Shop',
    startDate: '01/03/2026',
    expiryDate: '07/03/2026',
    discountPercentage: 0,
    fixedDiscountAmount: 50,
    currency: getTenant().currency.code,
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budgetType: 'fixed',
    budget: 5000,
    strictlyEnforceBudget: true,
    paymentMethods: ['Card', 'Mobile Banking'],
    cardTargetingMode: 'all',
    cardBrands: [],
    cardBin: '',
    cardIssuingCountry: '',
    cardType: 'All Types',
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BILASHOP2025',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 50,
    maxDiscount: 50,
    status: 'active'
  },
  BILA10MYR: {
    id: 'BILA10MYR',
    title: `BILA Mart Special Package (${getTenant().currency.code} 10 Discount)`,
    description: 'DISCOUNT_COUPON',
    startDate: '01/01/2026',
    expiryDate: '02/01/2026',
    discountPercentage: 0,
    fixedDiscountAmount: 10,
    currency: getTenant().currency.code,
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budgetType: 'fixed',
    budget: 11000,
    strictlyEnforceBudget: true,
    paymentMethods: ['Card'],
    cardTargetingMode: 'bins',
    cardBrands: ['Visa', 'Mastercard'],
    cardBin: '411111',
    cardIssuingCountry: '',
    cardType: 'All Types',
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BILA10MYR',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 10,
    maxDiscount: 10,
    status: 'active'
  },
  BULK2025: {
    id: 'BULK2025',
    title: 'Summer Festival Bulk Codes',
    description: 'Unique codes for summer festival attendees',
    startDate: '01/11/2025',
    expiryDate: '31/01/2026',
    discountPercentage: 15,
    fixedDiscountAmount: 0,
    currency: getTenant().currency.code,
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budgetType: 'fixed',
    budget: 50000,
    strictlyEnforceBudget: true,
    paymentMethods: ['Card', 'PromptPay'],
    cardTargetingMode: 'all',
    cardBrands: [],
    cardBin: '',
    cardIssuingCountry: '',
    cardType: 'All Types',
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: '',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Bulk',
    minTransactionValue: 500,
    maxTransactionValue: 999999,
    minDiscount: 0,
    maxDiscount: 1000,
    status: 'active'
  }
};
// Empty campaign template for new campaigns
const emptyCampaign: any = {
  id: '',
  title: '',
  description: '',
  startDate: '',
  expiryDate: '',
  discountPercentage: 0,
  fixedDiscountAmount: 0,
  currency: getTenant().currency.code,
  discountCarriedBy: 'Merchant',
  merchantPercentage: 0,
  budgetType: 'fixed',
  budget: 0,
  recurringBudget: 0,
  recurringBudgetPeriod: 'monthly',
  strictlyEnforceBudget: true,
  paymentMethods: [],
  cardTargetingMode: 'all',
  cardBrands: [],
  cardBin: '',
  cardIssuingCountry: '',
  cardIssuingCountries: [],
  cardType: 'All Types',
  validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  code: '',
  category: 'Discount code',
  owner: 'Merchant',
  codeStrategy: 'Global',
  minTransactionValue: 0,
  maxTransactionValue: 999999,
  minDiscount: 0,
  maxDiscount: 0,
  status: 'active'
};
const RECURRING_PERIODS = ['Daily', 'Weekly', 'Monthly'];
const CARD_TYPES = ['Credit', 'Debit', 'Prepaid'];
const CARD_CATEGORIES = ['Consumer', 'Commercial', 'Corporate', 'Premium', 'Platinum', 'Gold', 'Classic'];
const CARD_RULE_DEFINITIONS = [{
  id: 'brands',
  label: 'Card Brands',
  icon: CreditCardIcon,
  description: 'Filter by Visa, Mastercard, etc.'
}, {
  id: 'types',
  label: 'Card Type',
  icon: LayersIcon,
  description: 'Filter by Credit, Debit, Prepaid'
}, {
  id: 'bins',
  label: 'Card BIN',
  icon: HashIcon,
  description: 'Filter by Bank Identification Numbers'
}, {
  id: 'countries',
  label: 'Issuing Country',
  icon: GlobeIcon,
  description: 'Filter by card issuing country'
}, {
  id: 'categories',
  label: 'Card Category',
  icon: CreditCardIcon,
  description: 'Filter by Consumer, Commercial, etc.'
}] as const;
const PAYMENT_METHODS_LIST = ['Card', 'Mobile Banking', 'LINE Pay', 'Bank Rakyat Card', 'WeChat Pay', 'Alipay', 'TrueMoney Wallet', 'PromptPay', 'QR Payment', 'Installment', 'Internet Banking'];
const CARD_BRANDS = ['Visa', 'Mastercard', 'UnionPay', 'American Express', 'JCB', 'TPN', 'Discover', 'Diners Club'];
const COUNTRIES = [{
  code: 'TH',
  name: 'Thailand'
}, {
  code: 'US',
  name: 'United States'
}, {
  code: 'GB',
  name: 'United Kingdom'
}, {
  code: 'SG',
  name: 'Singapore'
}, {
  code: 'JP',
  name: 'Japan'
}, {
  code: 'CN',
  name: 'China'
}, {
  code: 'KR',
  name: 'South Korea'
}, {
  code: 'MY',
  name: 'Malaysia'
}, {
  code: 'ID',
  name: 'Indonesia'
}, {
  code: 'PH',
  name: 'Philippines'
}, {
  code: 'VN',
  name: 'Vietnam'
}, {
  code: 'IN',
  name: 'India'
}, {
  code: 'AU',
  name: 'Australia'
}, {
  code: 'NZ',
  name: 'New Zealand'
}, {
  code: 'HK',
  name: 'Hong Kong'
}, {
  code: 'TW',
  name: 'Taiwan'
}, {
  code: 'DE',
  name: 'Germany'
}, {
  code: 'FR',
  name: 'France'
}, {
  code: 'IT',
  name: 'Italy'
}, {
  code: 'ES',
  name: 'Spain'
}, {
  code: 'NL',
  name: 'Netherlands'
}, {
  code: 'CH',
  name: 'Switzerland'
}, {
  code: 'SE',
  name: 'Sweden'
}, {
  code: 'CA',
  name: 'Canada'
}, {
  code: 'BR',
  name: 'Brazil'
}, {
  code: 'MX',
  name: 'Mexico'
}, {
  code: 'AE',
  name: 'United Arab Emirates'
}, {
  code: 'SA',
  name: 'Saudi Arabia'
}, {
  code: 'RU',
  name: 'Russia'
}, {
  code: 'ZA',
  name: 'South Africa'
}, {
  code: 'KH',
  name: 'Cambodia'
}, {
  code: 'LA',
  name: 'Laos'
}, {
  code: 'MM',
  name: 'Myanmar'
}, {
  code: 'BD',
  name: 'Bangladesh'
}, {
  code: 'LK',
  name: 'Sri Lanka'
}];
interface ChoiceCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}
function ChoiceCard({
  title,
  description,
  icon,
  selected,
  onClick
}: ChoiceCardProps) {
  return <div onClick={onClick} className={`cursor-pointer rounded-lg border p-4 transition-all duration-200 flex items-start gap-3 ${selected ? 'border-[#E2231A] bg-[#FEE7E5] shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-11689">
      <div className="flex-shrink-0 mt-0.5" data-id="element-11690">
        <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selected ? 'border-[#E2231A] bg-[#E2231A]' : 'border-gray-300 bg-white'}`} data-id="element-11691">
          {selected && <div className="w-1.5 h-1.5 rounded-full bg-white" data-id="element-11692" />}
        </div>
      </div>
      <div className="flex-1" data-id="element-11693">
        <div className="flex items-center gap-2 mb-1" data-id="element-11694">
          <span className={`flex-shrink-0 ${selected ? 'text-[#9F140F]' : 'text-gray-500'}`} data-id="element-11695">
            {icon}
          </span>
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-11696">{title}</h3>
        </div>
        <p className="text-sm text-gray-500" data-id="element-11697">{description}</p>
      </div>
    </div>;
}
interface CountryMultiSelectProps {
  selectedCountries: Record<string, boolean>;
  onToggle: (code: string) => void;
  countrySearch: string;
  onSearchChange: (val: string) => void;
  filteredCountries: {
    code: string;
    name: string;
  }[];
  selectedCountryList: {
    code: string;
    name: string;
  }[];
}
function CountryMultiSelect({
  selectedCountries,
  onToggle,
  countrySearch,
  onSearchChange,
  filteredCountries,
  selectedCountryList
}: CountryMultiSelectProps) {
  const {
    t
  } = useTranslation('shops');
  const [isOpen, setIsOpen] = useState(false);
  return <div className="relative" data-id="element-11698">
      {/* Trigger / Display */}
      <div onClick={() => setIsOpen(!isOpen)} className="w-full min-h-[42px] px-3 py-2 border border-gray-200 rounded-lg text-sm cursor-pointer flex flex-wrap items-center gap-1.5 bg-white hover:border-gray-300 transition-colors" data-id="element-11699">
        {selectedCountryList.length > 0 ? <>
            {selectedCountryList.map(c => <span key={c.code} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#FEE7E5] border border-[#E2231A]/30 text-[#C41B14] text-xs font-medium" data-id="element-11700">
                {c.code}
                <button type="button" onClick={e => {
            e.stopPropagation();
            onToggle(c.code);
          }} className="text-[#C41B14]/60 hover:text-red-700 ml-0.5 leading-none" data-id="element-11701">
                  ×
                </button>
              </span>)}
          </> : <span className="text-gray-400" data-id="element-11702">
            {t('pages.CountryMultiSelect.select_countries')}
          </span>}
      </div>

      {/* Dropdown */}
      {isOpen && <>
          <div className="fixed inset-0 z-10" onClick={() => {
        setIsOpen(false);
        onSearchChange('');
      }} data-id="element-11703" />

          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-11704">
            <div className="p-2 border-b border-gray-100" data-id="element-11705">
              <div className="relative" data-id="element-11706">
                <SearchIcon size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-11707" />

                <input type="text" value={countrySearch} onChange={e => onSearchChange(e.target.value)} placeholder={t('pages.CountryMultiSelect.attrs.search_countries')} className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A] focus:outline-none" autoFocus onClick={e => e.stopPropagation()} data-id="element-11708" />
              </div>
            </div>
            <div className="max-h-52 overflow-y-auto" data-id="element-11709">
              {filteredCountries.length > 0 ? filteredCountries.map(country => <div key={country.code} onClick={e => {
            e.stopPropagation();
            onToggle(country.code);
          }} className={`flex items-center justify-between px-3 py-2 cursor-pointer text-sm transition-colors ${selectedCountries[country.code] ? 'bg-[#FEE7E5] text-[#C41B14]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-11710">
                    <span data-id="element-11711">
                      <span className="font-medium" data-id="element-11712">{country.code}</span>
                      <span className="ml-2 text-gray-500" data-id="element-11713">{country.name}</span>
                    </span>
                    {selectedCountries[country.code] && <span className="text-[#9F140F] text-xs font-bold" data-id="element-11714">
                        ✓
                      </span>}
                  </div>) : <div className="px-3 py-4 text-sm text-gray-400 text-center" data-id="element-11715">
                  {t('pages.CountryMultiSelect.no_countries_found')}
                </div>}
            </div>
          </div>
        </>}
    </div>;
}
export function CampaignForm() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [campaign, setCampaign] = useState<any>(emptyCampaign);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [discountType, setDiscountType] = useState<'fixed' | 'percentage'>('fixed');
  const [budgetType, setBudgetType] = useState<'fixed' | 'recurring'>('fixed');
  const [activeCardRules, setActiveCardRules] = useState<string[]>([]);
  const [showAddRuleMenu, setShowAddRuleMenu] = useState(false);
  const [selectedCardTypes, setSelectedCardTypes] = useState<Record<string, boolean>>({});
  const [selectedCardCategories, setSelectedCardCategories] = useState<Record<string, boolean>>({});
  const [selectedDays, setSelectedDays] = useState<Record<string, boolean>>({
    Sunday: true,
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
    Saturday: true
  });
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<Record<string, boolean>>({});
  const [selectedCardBrands, setSelectedCardBrands] = useState<Record<string, boolean>>(CARD_BRANDS.reduce((acc, brand) => {
    acc[brand] = true;
    return acc;
  }, {} as Record<string, boolean>));
  const [selectedCountries, setSelectedCountries] = useState<Record<string, boolean>>({});
  const [countrySearch, setCountrySearch] = useState('');
  useEffect(() => {
    const campaignId = searchParams.get('id');
    if (campaignId && mockCampaigns[campaignId]) {
      const loadedCampaign = mockCampaigns[campaignId];
      setCampaign(loadedCampaign);
      setIsEditing(true);
      setDiscountType(loadedCampaign.discountPercentage > 0 ? 'percentage' : 'fixed');
      setBudgetType(loadedCampaign.budgetType || 'fixed');
      // Load active card rules from campaign data
      const rules: string[] = [];
      if (loadedCampaign.cardBrands?.length > 0) rules.push('brands');
      if (loadedCampaign.cardType && loadedCampaign.cardType !== 'All Types') rules.push('types');
      if (loadedCampaign.cardBin) rules.push('bins');
      if (loadedCampaign.cardIssuingCountries?.length > 0) rules.push('countries');
      if (loadedCampaign.cardCategories?.length > 0) rules.push('categories');
      setActiveCardRules(rules);
      // Set selected days based on campaign data
      const days = loadedCampaign.validDays.reduce((acc: Record<string, boolean>, day: string) => {
        acc[day] = true;
        return acc;
      }, {});
      setSelectedDays(days);
      // Set selected payment methods
      const methods = (loadedCampaign.paymentMethods || []).reduce((acc: Record<string, boolean>, method: string) => {
        acc[method] = true;
        return acc;
      }, {});
      setSelectedPaymentMethods(methods);
      // Set selected card brands
      const brands = (loadedCampaign.cardBrands || []).reduce((acc: Record<string, boolean>, brand: string) => {
        acc[brand] = true;
        return acc;
      });
      setSelectedCardBrands(brands);
      // Set selected countries
      const countries = (loadedCampaign.cardIssuingCountries || []).reduce((acc: Record<string, boolean>, code: string) => {
        acc[code] = true;
        return acc;
      });
      setSelectedCountries(countries);
    }
    setLoading(false);
  }, [searchParams]);
  const navigateBack = () => {
    if (isEditing) {
      navigate(`/campaign-details?id=${campaign.id}`);
    } else {
      navigate('/campaigns');
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setCampaign({
        ...campaign,
        [name]: checkbox.checked
      });
      return;
    }
    if (type === 'number') {
      setCampaign({
        ...campaign,
        [name]: parseFloat(value) || 0
      });
      return;
    }
    setCampaign({
      ...campaign,
      [name]: value
    });
  };
  const handleDayToggle = (day: string) => {
    const newSelectedDays = {
      ...selectedDays,
      [day]: !selectedDays[day]
    };
    setSelectedDays(newSelectedDays);
    const validDays = Object.entries(newSelectedDays).filter(([_, isSelected]) => isSelected).map(([day]) => day);
    setCampaign({
      ...campaign,
      validDays
    });
  };
  const handlePaymentMethodToggle = (method: string) => {
    const newSelectedMethods = {
      ...selectedPaymentMethods,
      [method]: !selectedPaymentMethods[method]
    };
    setSelectedPaymentMethods(newSelectedMethods);
    const paymentMethods = Object.entries(newSelectedMethods).filter(([_, isSelected]) => isSelected).map(([method]) => method);
    setCampaign({
      ...campaign,
      paymentMethods
    });
  };
  const handleCardBrandToggle = (brand: string) => {
    const newSelectedBrands = {
      ...selectedCardBrands,
      [brand]: !selectedCardBrands[brand]
    };
    setSelectedCardBrands(newSelectedBrands);
    const cardBrands = Object.entries(newSelectedBrands).filter(([_, isSelected]) => isSelected).map(([brand]) => brand);
    setCampaign({
      ...campaign,
      cardBrands
    });
  };
  const handleCountryToggle = (code: string) => {
    const newSelectedCountries = {
      ...selectedCountries,
      [code]: !selectedCountries[code]
    };
    setSelectedCountries(newSelectedCountries);
    const cardIssuingCountries = Object.entries(newSelectedCountries).filter(([_, isSelected]) => isSelected).map(([code]) => code);
    setCampaign({
      ...campaign,
      cardIssuingCountries
    });
  };
  const handleCardTypeToggle = (type: string) => {
    const newSelected = {
      ...selectedCardTypes,
      [type]: !selectedCardTypes[type]
    };
    setSelectedCardTypes(newSelected);
    const cardTypes = Object.entries(newSelected).filter(([_, v]) => v).map(([k]) => k);
    setCampaign({
      ...campaign,
      cardTypes
    });
  };
  const handleCardCategoryToggle = (cat: string) => {
    const newSelected = {
      ...selectedCardCategories,
      [cat]: !selectedCardCategories[cat]
    };
    setSelectedCardCategories(newSelected);
    const cardCategories = Object.entries(newSelected).filter(([_, v]) => v).map(([k]) => k);
    setCampaign({
      ...campaign,
      cardCategories
    });
  };
  const addCardRule = (ruleId: string) => {
    if (!activeCardRules.includes(ruleId)) {
      setActiveCardRules([...activeCardRules, ruleId]);
    }
    setShowAddRuleMenu(false);
  };
  const removeCardRule = (ruleId: string) => {
    setActiveCardRules(activeCardRules.filter(r => r !== ruleId));
    // Clear the rule's data
    if (ruleId === 'brands') {
      setSelectedCardBrands({});
      setCampaign({
        ...campaign,
        cardBrands: []
      });
    } else if (ruleId === 'types') {
      setSelectedCardTypes({});
      setCampaign({
        ...campaign,
        cardTypes: []
      });
    } else if (ruleId === 'bins') {
      setCampaign({
        ...campaign,
        cardBin: ''
      });
    } else if (ruleId === 'countries') {
      setSelectedCountries({});
      setCampaign({
        ...campaign,
        cardIssuingCountries: []
      });
    } else if (ruleId === 'categories') {
      setSelectedCardCategories({});
      setCampaign({
        ...campaign,
        cardCategories: []
      });
    }
  };
  const availableRules = CARD_RULE_DEFINITIONS.filter(r => !activeCardRules.includes(r.id));
  const filteredCountries = COUNTRIES.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()) || c.code.toLowerCase().includes(countrySearch.toLowerCase()));
  const selectedCountryList = COUNTRIES.filter(c => selectedCountries[c.code]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submitting campaign:', {
      ...campaign,
      activeCardRules
    });
    navigate('/campaigns');
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64" data-id="element-11716">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E2231A]" data-id="element-11717"></div>
      </div>;
  }
  const inputClasses = 'w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] focus:outline-none transition-colors';
  const amountInputClasses = 'w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] focus:outline-none transition-colors';
  return <div className="max-w-4xl mx-auto space-y-6 pb-12" data-id="element-11718">
      {/* Header */}
      <div data-id="element-11719">
        <button onClick={navigateBack} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold mb-4" data-id="element-11720">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-11721" />
          {t('pages.CampaignDetails.back_to_campaigns')}
        </button>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-11722">
          {isEditing ? 'Update Campaign' : 'Create Campaign'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-11723">
        {/* Section 1: Campaign Details */}
        <div className="p-6 border-b border-gray-100" data-id="element-11724">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-11725">
            {t('pages.CampaignDetails.campaign_details')}
          </h2>
          <div className="space-y-6" data-id="element-11726">
            <FormField label={t('pages.CampaignForm.attrs.title')} required data-id="element-11727">
              <input type="text" name="title" value={campaign.title} onChange={handleInputChange} required placeholder={t('pages.CampaignForm.attrs.eg_summer_sale_2025')} className={inputClasses} data-id="element-11728" />
            </FormField>

            <FormField label={t('pages.CampaignDetails.description')} data-id="element-11729">
              <textarea name="description" value={campaign.description} onChange={handleInputChange} rows={3} placeholder={t('pages.CampaignForm.attrs.brief_description_of_the_campaign')} className={`${inputClasses} resize-none`} data-id="element-11730" />
            </FormField>
          </div>
        </div>

        {/* Section 2: Code Strategy */}
        <div className="p-6 border-b border-gray-100" data-id="element-11731">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-11732">
            {t('pages.CampaignDetails.campaign_code')}
          </h2>
          <div className="space-y-6" data-id="element-11733">
            <FormField label={t('components.CampaignList.code_strategy')} required data-id="element-11734">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11735">
                <ChoiceCard title={t('pages.CampaignForm.attrs.single_global_code')} description={t('pages.CampaignForm.attrs.one_code_that_can_be_used')} icon={<TagIcon size={18} data-id="element-11737" />} selected={campaign.codeStrategy === 'Global'} onClick={() => setCampaign({
                ...campaign,
                codeStrategy: 'Global'
              })} data-id="element-11736" />

                <ChoiceCard title={t('pages.CampaignForm.attrs.unique_bulk_codes')} description={t('pages.CampaignForm.attrs.system_generates_multiple_unique_codes_for')} icon={<TagsIcon size={18} data-id="element-11739" />} selected={campaign.codeStrategy === 'Bulk'} onClick={() => setCampaign({
                ...campaign,
                codeStrategy: 'Bulk'
              })} data-id="element-11738" />
              </div>
            </FormField>

            {campaign.codeStrategy === 'Global' && <FormField label={t('pages.CampaignForm.attrs.global_campaign_code')} required data-id="element-11740">
                <input type="text" name="code" value={campaign.code} onChange={handleInputChange} required placeholder={t('pages.CampaignForm.attrs.eg_summer25')} className={inputClasses} data-id="element-11741" />
              </FormField>}
          </div>
        </div>

        {/* Section 3: Discount Configuration */}
        <div className="p-6 border-b border-gray-100" data-id="element-11742">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-11743">
            {t('pages.CampaignForm.discount_configuration')}
          </h2>
          <div className="space-y-6" data-id="element-11744">
            <FormField label={t('pages.CampaignForm.attrs.discount_type')} required data-id="element-11745">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11746">
                <ChoiceCard title={t('pages.CampaignForm.attrs.fixed_amount')} description={t('pages.CampaignForm.attrs.a_specific_usd_amount_off_the')} icon={<BanknoteIcon size={18} data-id="element-11748" />} selected={discountType === 'fixed'} onClick={() => {
                setDiscountType('fixed');
                setCampaign({
                  ...campaign,
                  discountPercentage: 0
                });
              }} data-id="element-11747" />

                <ChoiceCard title={t('pages.CampaignForm.attrs.percentage')} description={t('pages.CampaignForm.attrs.a_percentage_off_the_total_purchase')} icon={<PercentIcon size={18} data-id="element-11750" />} selected={discountType === 'percentage'} onClick={() => {
                setDiscountType('percentage');
                setCampaign({
                  ...campaign,
                  fixedDiscountAmount: 0
                });
              }} data-id="element-11749" />
              </div>
            </FormField>

            {discountType === 'fixed' ? <FormField label={t('pages.CampaignForm.attrs.discount_amount')} required data-id="element-11751">
                <div className="relative" data-id="element-11752">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11753">
                    <span className="text-gray-500 font-medium" data-id="element-11754">
                      {campaign.currency}
                    </span>
                  </div>
                  <input type="number" name="fixedDiscountAmount" value={campaign.fixedDiscountAmount || ''} onChange={handleInputChange} required min="0" step="0.01" placeholder="0.00" className={amountInputClasses} data-id="element-11755" />
                </div>
              </FormField> : <FormField label={t('pages.CampaignForm.attrs.discount_percentage')} required data-id="element-11756">
                <div className="relative" data-id="element-11757">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11758">
                    <span className="text-gray-500 font-medium" data-id="element-11759">%</span>
                  </div>
                  <input type="number" name="discountPercentage" value={campaign.discountPercentage || ''} onChange={handleInputChange} required min="0" max="100" step="0.1" placeholder="0" className={amountInputClasses} data-id="element-11760" />
                </div>
              </FormField>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11761">
              <FormField label={t('pages.CampaignForm.attrs.minimum_transaction_value')} data-id="element-11762">
                <div className="relative" data-id="element-11763">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11764">
                    <span className="text-gray-500 font-medium" data-id="element-11765">
                      {campaign.currency}
                    </span>
                  </div>
                  <input type="number" name="minTransactionValue" value={campaign.minTransactionValue || ''} onChange={handleInputChange} min="0" step="0.01" placeholder="0.00" className={amountInputClasses} data-id="element-11766" />
                </div>
              </FormField>

              {discountType === 'percentage' && <FormField label={t('pages.CampaignForm.attrs.maximum_discount_amount')} data-id="element-11767">
                  <div className="relative" data-id="element-11768">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11769">
                      <span className="text-gray-500 font-medium" data-id="element-11770">
                        {campaign.currency}
                      </span>
                    </div>
                    <input type="number" name="maxDiscount" value={campaign.maxDiscount || ''} onChange={handleInputChange} min="0" step="0.01" placeholder="0.00" className={amountInputClasses} data-id="element-11771" />
                  </div>
                </FormField>}
            </div>
          </div>
        </div>

        {/* Section 4: Budget & Schedule */}
        <div className="p-6 border-b border-gray-100" data-id="element-11772">
          <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wider" data-id="element-11773">
            {t('pages.CampaignForm.budget_schedule')}
          </h2>
          <div className="space-y-6" data-id="element-11774">
            <FormField label={t('pages.CampaignForm.attrs.budget_type')} data-id="element-11775">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11776">
                <ChoiceCard title={t('pages.CampaignForm.attrs.fixed_budget')} description={t('pages.CampaignForm.attrs.a_single_budget_for_the_entire')} icon={<BanknoteIcon size={18} data-id="element-11778" />} selected={budgetType === 'fixed'} onClick={() => {
                setBudgetType('fixed');
                setCampaign({
                  ...campaign,
                  budgetType: 'fixed'
                });
              }} data-id="element-11777" />

                <ChoiceCard title={t('pages.CampaignForm.attrs.recurring_budget')} description={t('pages.CampaignForm.attrs.budget_resets_daily_weekly_or_monthly')} icon={<RefreshCwIcon size={18} data-id="element-11780" />} selected={budgetType === 'recurring'} onClick={() => {
                setBudgetType('recurring');
                setCampaign({
                  ...campaign,
                  budgetType: 'recurring'
                });
              }} data-id="element-11779" />
              </div>
            </FormField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11781">
              {budgetType === 'fixed' ? <FormField label={t('pages.CampaignForm.attrs.total_budget')} required data-id="element-11782">
                  <div className="relative" data-id="element-11783">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11784">
                      <span className="text-gray-500 font-medium" data-id="element-11785">
                        {campaign.currency}
                      </span>
                    </div>
                    <input type="number" name="budget" value={campaign.budget || ''} onChange={handleInputChange} required min="0" step="0.01" placeholder="0.00" className={amountInputClasses} data-id="element-11786" />
                  </div>
                </FormField> : <>
                  <FormField label={t('pages.CampaignForm.attrs.recurring_budget_amount')} required data-id="element-11787">
                    <div className="relative" data-id="element-11788">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11789">
                        <span className="text-gray-500 font-medium" data-id="element-11790">
                          {campaign.currency}
                        </span>
                      </div>
                      <input type="number" name="recurringBudget" value={campaign.recurringBudget || ''} onChange={handleInputChange} required min="0" step="0.01" placeholder="0.00" className={amountInputClasses} data-id="element-11791" />
                    </div>
                  </FormField>
                  <FormField label={t('pages.CampaignForm.attrs.reset_period')} required data-id="element-11792">
                    <select name="recurringBudgetPeriod" value={campaign.recurringBudgetPeriod} onChange={handleInputChange as any} className={inputClasses} data-id="element-11793">
                      {RECURRING_PERIODS.map(period => <option key={period} value={period.toLowerCase()} data-id="element-11794">
                          {period}
                        </option>)}
                    </select>
                  </FormField>
                </>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11795">
              <FormField label={t('pages.CampaignForm.attrs.start_date')} required data-id="element-11796">
                <div className="relative" data-id="element-11797">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11798">
                    <CalendarIcon size={16} className="text-gray-400" data-id="element-11799" />
                  </div>
                  <input type="date" name="startDate" value={campaign.startDate ? campaign.startDate.split('/').reverse().join('-') : ''} onChange={e => {
                  const date = e.target.value;
                  if (date) {
                    const [year, month, day] = date.split('-');
                    setCampaign({
                      ...campaign,
                      startDate: `${day}/${month}/${year}`
                    });
                  } else {
                    setCampaign({
                      ...campaign,
                      startDate: ''
                    });
                  }
                }} required className={amountInputClasses} data-id="element-11800" />
                </div>
              </FormField>

              <FormField label={t('pages.CampaignForm.attrs.end_date')} data-id="element-11801">
                <div className="relative" data-id="element-11802">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-11803">
                    <CalendarIcon size={16} className="text-gray-400" data-id="element-11804" />
                  </div>
                  <input type="date" name="expiryDate" value={campaign.expiryDate ? campaign.expiryDate.split('/').reverse().join('-') : ''} onChange={e => {
                  const date = e.target.value;
                  if (date) {
                    const [year, month, day] = date.split('-');
                    setCampaign({
                      ...campaign,
                      expiryDate: `${day}/${month}/${year}`
                    });
                  } else {
                    setCampaign({
                      ...campaign,
                      expiryDate: ''
                    });
                  }
                }} className={amountInputClasses} data-id="element-11805" />
                </div>
              </FormField>
            </div>

            <FormField label={t('pages.CampaignForm.attrs.valid_days')} data-id="element-11806">
              <div className="flex flex-wrap gap-2" data-id="element-11807">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => <button key={day} type="button" onClick={() => handleDayToggle(day)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedDays[day] ? 'bg-[#FEE7E5] text-[#C41B14] border border-[#E2231A]/30' : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'}`} data-id="element-11808">
                    {day.substring(0, 3)}
                  </button>)}
              </div>
            </FormField>
          </div>
        </div>

        {/* Section 5: Targeting Rules */}
        <div className="p-6" data-id="element-11809">
          <div className="flex items-center justify-between mb-6" data-id="element-11810">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-11811">
              {t('pages.CampaignForm.targeting_rules')}
            </h2>
            <div className="relative" data-id="element-11812">
              <button type="button" onClick={() => setShowAddRuleMenu(!showAddRuleMenu)} disabled={availableRules.length === 0} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${availableRules.length === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#FEE7E5] text-[#9F140F] hover:bg-[#F1786F]'}`} data-id="element-11813">
                <PlusIcon size={16} data-id="element-11814" />
                {t('pages.CampaignForm.attrs.add_rule')}
              </button>

              {showAddRuleMenu && availableRules.length > 0 && <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowAddRuleMenu(false)} data-id="element-11815" />

                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-11816">
                    {availableRules.map(rule => <button key={rule.id} type="button" onClick={() => addCardRule(rule.id)} className="w-full flex items-start px-4 py-3 hover:bg-gray-50 text-left" data-id="element-11817">
                        <rule.icon size={18} className="text-gray-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-11818" />

                        <div data-id="element-11819">
                          <div className="text-sm font-medium text-gray-900" data-id="element-11820">
                            {rule.label}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-11821">
                            {rule.description}
                          </div>
                        </div>
                      </button>)}
                  </div>
                </>}
            </div>
          </div>

          <div className="space-y-6" data-id="element-11822">
            <FormField label={t('pages.CampaignForm.attrs.payment_methods')} data-id="element-11823">
              <div className="flex flex-wrap gap-2" data-id="element-11824">
                {PAYMENT_METHODS_LIST.map(method => <button key={method} type="button" onClick={() => handlePaymentMethodToggle(method)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedPaymentMethods[method] ? 'bg-[#FEE7E5] text-[#C41B14] border border-[#E2231A]/30' : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'}`} data-id="element-11825">
                    {method}
                  </button>)}
              </div>
            </FormField>

            {activeCardRules.length === 0 ? <div className="bg-gray-50 border border-gray-200 border-dashed rounded-lg p-6 text-center" data-id="element-11826">
                <CreditCardIcon size={24} className="mx-auto text-gray-400 mb-2" data-id="element-11827" />

                <h3 className="text-sm font-medium text-gray-900 mb-1" data-id="element-11828">
                  {t('pages.CampaignForm.no_card_targeting_rules')}
                </h3>
                <p className="text-sm text-gray-500" data-id="element-11829">
                  {t('pages.CampaignForm.attrs.this_campaign_will_apply_to')}
                </p>
              </div> : <div className="space-y-4" data-id="element-11830">
                {activeCardRules.includes('brands') && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 relative group" data-id="element-11831">
                    <button type="button" onClick={() => removeCardRule('brands')} className="absolute top-4 right-4 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100" data-id="element-11832">
                      <XIcon size={16} data-id="element-11833" />
                    </button>
                    <FormField label={t('pages.CampaignForm.attrs.eligible_card_brands')} data-id="element-11834">
                      <div className="flex flex-wrap gap-2 mt-2" data-id="element-11835">
                        {CARD_BRANDS.map(brand => <button key={brand} type="button" onClick={() => handleCardBrandToggle(brand)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedCardBrands[brand] ? 'bg-[#FEE7E5] text-[#C41B14] border border-[#E2231A]/30' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`} data-id="element-11836">
                            {brand}
                          </button>)}
                      </div>
                    </FormField>
                  </div>}

                {activeCardRules.includes('types') && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 relative group" data-id="element-11837">
                    <button type="button" onClick={() => removeCardRule('types')} className="absolute top-4 right-4 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100" data-id="element-11838">
                      <XIcon size={16} data-id="element-11839" />
                    </button>
                    <FormField label={t('pages.CampaignForm.attrs.eligible_card_types')} data-id="element-11840">
                      <div className="flex flex-wrap gap-2 mt-2" data-id="element-11841">
                        {CARD_TYPES.map(type => <button key={type} type="button" onClick={() => handleCardTypeToggle(type)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedCardTypes[type] ? 'bg-[#FEE7E5] text-[#C41B14] border border-[#E2231A]/30' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`} data-id="element-11842">
                            {type}
                          </button>)}
                      </div>
                    </FormField>
                  </div>}

                {activeCardRules.includes('categories') && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 relative group" data-id="element-11843">
                    <button type="button" onClick={() => removeCardRule('categories')} className="absolute top-4 right-4 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100" data-id="element-11844">
                      <XIcon size={16} data-id="element-11845" />
                    </button>
                    <FormField label={t('pages.CampaignForm.attrs.eligible_card_categories')} data-id="element-11846">
                      <div className="flex flex-wrap gap-2 mt-2" data-id="element-11847">
                        {CARD_CATEGORIES.map(cat => <button key={cat} type="button" onClick={() => handleCardCategoryToggle(cat)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedCardCategories[cat] ? 'bg-[#FEE7E5] text-[#C41B14] border border-[#E2231A]/30' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`} data-id="element-11848">
                            {cat}
                          </button>)}
                      </div>
                    </FormField>
                  </div>}

                {activeCardRules.includes('bins') && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 relative group" data-id="element-11849">
                    <button type="button" onClick={() => removeCardRule('bins')} className="absolute top-4 right-4 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100" data-id="element-11850">
                      <XIcon size={16} data-id="element-11851" />
                    </button>
                    <FormField label={t('pages.CampaignForm.attrs.target_card_bins')} data-id="element-11852">
                      <input type="text" name="cardBin" value={campaign.cardBin || ''} onChange={handleInputChange} placeholder={t('pages.CampaignForm.attrs.enter_first_6_or_8_digits')} className={`${inputClasses} bg-white mt-2`} data-id="element-11853" />
                    </FormField>
                  </div>}

                {activeCardRules.includes('countries') && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 relative group" data-id="element-11854">
                    <button type="button" onClick={() => removeCardRule('countries')} className="absolute top-4 right-4 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover:opacity-100" data-id="element-11855">
                      <XIcon size={16} data-id="element-11856" />
                    </button>
                    <FormField label={t('pages.CampaignForm.attrs.eligible_issuing_countries')} data-id="element-11857">
                      <div className="mt-2" data-id="element-11858">
                        <CountryMultiSelect selectedCountries={selectedCountries} onToggle={handleCountryToggle} countrySearch={countrySearch} onSearchChange={setCountrySearch} filteredCountries={filteredCountries} selectedCountryList={selectedCountryList} data-id="element-11859" />
                      </div>
                    </FormField>
                  </div>}
              </div>}
          </div>
        </div>

        {/* Form Actions */}
        <div className="p-6 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3" data-id="element-11860">
          <button type="button" onClick={navigateBack} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-11861">
            {t('pages.CampaignDetails.cancel')}
          </button>
          <button type="submit" className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#EB4A42] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11862">
            {isEditing ? 'Save Changes' : 'Create Campaign'}
          </button>
        </div>
      </form>
    </div>;
}